import React, {
  ReactNode,
  RefObject,
  useEffect,
  useState,
  ComponentPropsWithRef,
} from 'react';
import Head from 'next/head';
import PubSub from 'pubsub-js';
import Link from 'next/link';

import { useCommunityContext } from 'components/CommunityContext';
import ProfilePanel from 'components/Profile/ProfilePanel';
import { eventNames } from 'constants/eventNames';
import EventPanel from 'components/Events/components/EventPanel/EventPanel';
import useEventPanel from 'hooks/Events/useEventPanel';
import { setGlobalCSSVariables } from 'helpers/styleHelpers';
import ModeratorPanel from 'components/ModeratorPanel';
import ReplyPanel from 'components/ReplyPanel/ReplyPanel';
import { ChalkCustomAnchorProvider, ChalkThemeProvider } from '@thinkific/chalk/react-providers';
import { Post } from '__generated__/graphql/legacy/graphql';
import { ReplyEditorProvider } from 'components/ReplyEditorContext/ReplyEditorContext';
import { MAP_FONTS_TO_CHALK } from 'utils/constants';
import Layout from 'components/new/layout';
import UserAvatarMenu from 'components/UserAvatarMenu/UserAvatarMenu';
import GlobalStateProvider from 'components/new/providers/GlobalStateProvider';

interface PubSubInfo {
  userId: string;
  lastElementRef: RefObject<HTMLElement | null>;
  post?: Post;
  commentIdFromUrl?: string;
}

function NextjsLinkWrapper({ href, ...props }: ComponentPropsWithRef<'a'>) {
  // needed due to Link `href` prop not accepting `undefined` (but `<a … />` replacement should be able to accept it)
  return <Link {...props} href={href ?? ''} />;
}

const SiteLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isProfilePanelOpen, setIsProfilePanelOpen] = useState<boolean>(false);
  const [isModeratorPanelOpen, setIsModeratorPanelOpen] = useState<boolean>(
    false
  );
  const [isUserAvatarMenuOpen, setIsUserAvatarMenuOpen] = useState<boolean>(
    false
  );
  const [isReplyPanelOpen, setIsReplyPanelOpen] = useState<boolean>(false);
  const [lastFocusedElementRef, setLastFocusedElementRef] = useState<
    RefObject<HTMLElement | null>
  >();
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedPost, setSelectedPost] = useState<Post | undefined>();
  const { community, site, currentUser } = useCommunityContext();
  const {
    eventId,
    handleCloseEventPanel,
    isOpen: isEventPanelOpen,
  } = useEventPanel(() => setIsProfilePanelOpen(false));

  useEffect(() => {
    setGlobalCSSVariables(community.primaryColor);
  }, [community]);

  useEffect(() => {
    const profileToken = PubSub.subscribe(
      eventNames.OPEN_PROFILE_PANEL,
      (msg: string, info: PubSubInfo) => {
        handleOpenProfilePanel(info);
      }
    );

    const sideMenuToken = PubSub.subscribe(
      eventNames.OPEN_SIDE_MENU,
      (msg: string, info: PubSubInfo) => {
        handleOpenUserAvatarMenu(info);
      }
    );

    const moderatorToken = PubSub.subscribe(
      eventNames.OPEN_MODERATOR_PANEL,
      (msg: string, info: PubSubInfo) => {
        handleOpenModeratorPanel(info);
      }
    );

    const replyToken = PubSub.subscribe(
      eventNames.OPEN_REPLY_PANEL,
      (msg: string, info: PubSubInfo) => {
        handleOpenReplyPanel(info);
      }
    );

    return () => {
      PubSub.unsubscribe(profileToken);
      PubSub.unsubscribe(sideMenuToken);
      PubSub.unsubscribe(moderatorToken);
      PubSub.unsubscribe(replyToken);
    };
  }, []);

  const handleCloseProfilePanel = () => {
    lastFocusedElementRef?.current?.focus();
    setSelectedUserId('');
    setIsProfilePanelOpen(false);
  };

  const handleOpenProfilePanel = (info: PubSubInfo) => {
    handleCloseEventPanel();
    setLastFocusedElementRef(info.lastElementRef);
    setSelectedUserId(info.userId);
    setIsProfilePanelOpen(true);
  };

  const handleOpenUserAvatarMenu = (info: PubSubInfo) => {
    handleCloseEventPanel();
    setLastFocusedElementRef(info.lastElementRef);
    setSelectedUserId(info.userId);
    setIsUserAvatarMenuOpen(true);
  };

  const handleCloseUserAvatarMenu = () => {
    lastFocusedElementRef?.current?.focus();
    setSelectedUserId('');
    setIsUserAvatarMenuOpen(false);
  };

  const handleOpenModeratorPanel = (info: PubSubInfo) => {
    setLastFocusedElementRef(info.lastElementRef);
    setIsModeratorPanelOpen(true);
  };

  const handleCloseModeratorPanel = () => {
    lastFocusedElementRef?.current?.focus();
    setIsModeratorPanelOpen(false);
  };

  const handleOpenReplyPanel = (info: PubSubInfo) => {
    document.body.style.overflow = 'hidden';
    setLastFocusedElementRef(info.lastElementRef);
    setSelectedPost(info?.post);
    setIsReplyPanelOpen(true);
  };

  const handleCloseReplyPanel = () => {
    document.body.style.overflow = 'visible';
    lastFocusedElementRef?.current?.focus();
    setIsReplyPanelOpen(false);
  };

  return (
    <>
      <Head>
        <title>{community.name}</title>
        <meta content={community.name} name="description" />
        <link href={site.favicon} rel="shortcut icon" type="image/x-icon" />
      </Head>
      <GlobalStateProvider>
        <ChalkThemeProvider
          bodyFont={MAP_FONTS_TO_CHALK[community.font]}
          primaryColor={community.primaryColor}
          titleFont={MAP_FONTS_TO_CHALK[community.font]}
        >
          <ChalkCustomAnchorProvider value={NextjsLinkWrapper}>
            <Layout.GlobalHeader
              community={community}
              user={{
                ...currentUser,
                username: currentUser.fullName,
                userId: currentUser.id,
              }}
            />
            {children}
            <ProfilePanel
              isOpen={isProfilePanelOpen}
              userId={selectedUserId}
              onClose={handleCloseProfilePanel}
            />
            <UserAvatarMenu
              communityId={community.id}
              isOpen={isUserAvatarMenuOpen}
              user={currentUser}
              onClose={handleCloseUserAvatarMenu}
            />
            <EventPanel
              eventId={eventId}
              isOpen={isEventPanelOpen}
              onClose={handleCloseEventPanel}
            />
            <ModeratorPanel
              isOpen={isModeratorPanelOpen}
              onClose={handleCloseModeratorPanel}
            />
            <ReplyEditorProvider>
              <ReplyPanel
                isOpen={isReplyPanelOpen}
                postId={selectedPost?.id}
                onClose={handleCloseReplyPanel}
              />
            </ReplyEditorProvider>
            <Layout.MobileChin community={community} />
          </ChalkCustomAnchorProvider>
        </ChalkThemeProvider>
      </GlobalStateProvider>
    </>
  );
};

export const getLayout = (page: ReactNode): ReactNode => (
  <SiteLayout>{page}</SiteLayout>
);

export default SiteLayout;
